import { useApolloClient } from '@apollo/client';
import { InfoTooltip, SelectBox, TextBox } from '@homex/se-react-components';
import { FlowCarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';
import { filterXSS } from 'xss';

import {
  setContainsXssInput,
  setCurrentCarouselItem,
  setIsAuthorizedForRepairs,
  setServiceAddress,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { ReactComponent as InputDoorSvg } from 'components/svg/InputDoor.svg';
import { ReactComponent as InputMailboxSvg } from 'components/svg/InputMailbox.svg';
import { ReactComponent as InputPinSvg } from 'components/svg/InputPin.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  CountryCode,
  ICarouselItemAddressBottomProps,
  INVALID_INPUT_MESSAGE,
  IState,
  ServiceType,
} from 'typings';
import {
  IGetAddressBookingStatusForCustomerQuery,
  IGetAddressBookingStatusForCustomerQueryVariables,
  IGetCorrectedAddressesQuery,
  IGetCorrectedAddressesQueryVariables,
} from 'typings/GraphQL';
import { australiaSelectBoxOptions } from 'utils/australiaSelectBoxOptions';
import { canadaProvinceSelectBoxOptions } from 'utils/canadaProvinceSelectBoxOptions';
import { unitedStatesSelectBoxOptions } from 'utils/unitedStatesSelectBoxOptions';

import {
  Buttons,
  CheckboxStyled,
  Container,
  FormGroup,
  NoteContainer,
  TextBoxGroup,
} from './styles';

const getAddressBookingStatusForCustomerQuery = loader(
  '../../queries/getAddressBookingStatusForCustomer.graphql',
);
const getCorrectedAddressesQuery = loader(
  '../../queries/getCorrectedAddresses.graphql',
);

export const CarouselItemAddressBottom = ({
  addressDisclaimerEnabled,
  correctionNeededDestinationId,
  matchFoundDestinationId,
  matchNotFoundDestinationId,
  parentCarouselItemId,
}: ICarouselItemAddressBottomProps) => {
  const { query } = useApolloClient();
  const dispatch = useThunkDispatch();
  const {
    addressState = '',
    city = '',
    countryCode,
    flow,
    isAuthorizedForRepairs = false,
    phoneNumber,
    serviceType,
    street1 = '',
    street2 = '',
    zipCode,
  } = useSelector((state: IState) => ({
    addressState: state.userInput.address.state,
    city: state.userInput.address.city,
    countryCode: state.userInput.address.countryCode,
    flow: state.client.flow,
    isAuthorizedForRepairs: state.userInput.isAuthorizedForRepairs,
    phoneNumber: state.userInput.customerPhone.number,
    serviceType: state.userInput.serviceType,
    street1: state.userInput.address.street1,
    street2: state.userInput.address.street2,
    zipCode: state.userInput.address.zipCode,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>('');
  const theme = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? theme.buttonType;

  const [inputFieldError, setInputFieldError] = useState<Array<boolean>>([
    street1 !== filterXSS(street1),
    street2 !== filterXSS(street2 || ''),
    city !== filterXSS(city),
  ]);

  const checkForXss = (s: string, field: number) => {
    const newInputErrorField = inputFieldError;

    if (s !== filterXSS(s)) {
      dispatch(setContainsXssInput(true));
      newInputErrorField[field] = true;
      setInputFieldError(newInputErrorField);
    } else if (newInputErrorField[field]) {
      newInputErrorField[field] = false;
      const noErrors = newInputErrorField.reduce(
        (prev, current) => prev && !current,
        true,
      );

      if (noErrors) {
        dispatch(setContainsXssInput(false));
      }

      setInputFieldError(newInputErrorField);
    }
  };

  const isCheckBoxPulsing = !!(
    street1 &&
    city &&
    addressState &&
    !isAuthorizedForRepairs
  );
  const isFormValid = !!(
    street1 &&
    city &&
    addressState &&
    isAuthorizedForRepairs &&
    !errorMessage
  );

  const getStateLabel = () => {
    switch (countryCode) {
      case CountryCode.CA: {
        return Phrase({ id: 'SALES_ADDRESS.PROVINCE.LABEL' });
      }

      case CountryCode.US: {
        return Phrase({ id: 'SALES_ADDRESS.STATE.LABEL' });
      }

      default: {
        return Phrase({ id: 'SALES_ADDRESS.STATE.LABEL' });
      }
    }
  };

  const getStateOptions = () => {
    switch (countryCode) {
      case CountryCode.AU:
        return australiaSelectBoxOptions;
      case CountryCode.CA:
        return canadaProvinceSelectBoxOptions;
      case CountryCode.US:
        return unitedStatesSelectBoxOptions;
      default:
        return unitedStatesSelectBoxOptions;
    }
  };

  const hasInputError = inputFieldError.reduce(
    (prev, hasError) => prev || hasError,
    false,
  );

  const handleStreet1Change = (event: React.FormEvent<HTMLInputElement>) => {
    const preFilterText = event.currentTarget.value;
    dispatch(setServiceAddress({ street1: preFilterText }));
    checkForXss(preFilterText, 0);

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleStreet2Change = (event: React.FormEvent<HTMLInputElement>) => {
    const preFilterText = event.currentTarget.value;
    dispatch(setServiceAddress({ street2: preFilterText }));
    checkForXss(preFilterText, 1);

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setIsAuthorizedForRepairs(event.currentTarget.checked));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleCityChange = (event: React.FormEvent<HTMLInputElement>) => {
    const preFilterText = event.currentTarget.value;
    dispatch(setServiceAddress({ city: preFilterText }));
    checkForXss(preFilterText, 2);

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const addressError = inputFieldError[0] ? INVALID_INPUT_MESSAGE : undefined;
  const unitError = inputFieldError[1] ? INVALID_INPUT_MESSAGE : undefined;
  const cityError = inputFieldError[2] ? INVALID_INPUT_MESSAGE : undefined;

  const handleContinueClick = async () => {
    if (
      !city ||
      !addressState ||
      !street1 ||
      !zipCode ||
      !isAuthorizedForRepairs
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const {
        data: getCorrectedAddressesData,
        error: getCorrectedAddressesError,
      } = await query<
        IGetCorrectedAddressesQuery,
        IGetCorrectedAddressesQueryVariables
      >({
        query: getCorrectedAddressesQuery,
        variables: {
          address: { city, state: addressState, street1, street2, zipCode },
        },
      });

      if (getCorrectedAddressesError) {
        setErrorMessage(Phrase({ id: 'GENERIC_GRAPHQL_ERROR' }));
        setIsLoading(false);

        return;
      }

      const correctedAddresses =
        getCorrectedAddressesData?.getCorrectedAddresses?.correctedAddresses ||
        [];

      const correctedAddressMatch = correctedAddresses.find(
        ({ isMatch }) => isMatch,
      );

      let nextErrorMessage: React.ReactNode = '';

      if (correctedAddressMatch) {
        const {
          data: getAddressBookingStatusForCustomerData,
          error: getAddressBookingStatusForCustomerError,
        } = await query<
          IGetAddressBookingStatusForCustomerQuery,
          IGetAddressBookingStatusForCustomerQueryVariables
        >({
          query: getAddressBookingStatusForCustomerQuery,
          variables: {
            address: { city, state: addressState, street1, street2, zipCode },
            enterpriseVersion: theme.enterpriseEnabled
              ? theme.enterpriseVersion
              : undefined,
            phoneNumber: phoneNumber ?? '',
            serviceType: serviceType ?? ServiceType.REPAIR,
          },
        });

        const isBookingAllowed =
          getAddressBookingStatusForCustomerData
            ?.getAddressBookingStatusForCustomer?.isBookingAllowed !== false;
        const isServiceable =
          getAddressBookingStatusForCustomerData
            ?.getAddressBookingStatusForCustomer?.isServiceable !== false;

        nextErrorMessage = !isBookingAllowed
          ? Phrase({ id: 'CUSTOMER_LOOKUP.PHONE.NOBOOK.HTML' })
          : !isServiceable
          ? Phrase({ id: 'SERVICE_AREA.ERROR.HTML' })
          : getAddressBookingStatusForCustomerError
          ? Phrase({ id: 'GENERIC_GRAPHQL_ERROR' })
          : '';
      }

      setErrorMessage(nextErrorMessage);
      setIsLoading(false);

      if (!nextErrorMessage) {
        let finalDestinationItemId: FlowCarouselItemId;

        if (correctedAddresses.length === 0) {
          finalDestinationItemId = matchNotFoundDestinationId;
        } else if (correctedAddressMatch) {
          dispatch(setServiceAddress(correctedAddressMatch.address));
          finalDestinationItemId = matchFoundDestinationId;
        } else {
          finalDestinationItemId = correctionNeededDestinationId;
        }

        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            finalDestinationItemId,
          ),
        );
      }
    } catch {
      setErrorMessage(Phrase({ id: 'GENERIC_GRAPHQL_ERROR' }));
      setIsLoading(false);
    }
  };

  const handleStateChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setServiceAddress({ state: event.currentTarget.value }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  return (
    <FormGroup>
      <Container>
        <TextBox
          autoComplete="address-line1"
          errorMessage={addressError}
          label={Phrase({ id: 'SALES_ADDRESS.ADDRESS1.LABEL' })}
          leftIcon={InputMailboxSvg}
          name="address"
          onChange={handleStreet1Change}
          tooltipVertical="bottom"
          value={street1}
        />
        <TextBox
          autoComplete="address-line2"
          errorMessage={unitError}
          label={Phrase({ id: 'SALES_ADDRESS.ADDRESS2.LABEL' })}
          leftIcon={InputDoorSvg}
          onChange={handleStreet2Change}
          value={street2 || ''}
        />
        <TextBoxGroup>
          <TextBox
            autoComplete="address-level2"
            errorMessage={cityError}
            label={Phrase({ id: 'SALES_ADDRESS.CITY.LABEL' })}
            leftIcon={InputPinSvg}
            name="city"
            onChange={handleCityChange}
            value={city}
          />
          <SelectBox
            autoComplete="address-level1"
            label={getStateLabel()}
            name="state"
            onChange={handleStateChange}
            options={getStateOptions()}
            value={addressState}
            width="65px"
          />
        </TextBoxGroup>
        <CheckboxStyled
          checked={isAuthorizedForRepairs}
          isPulsing={isCheckBoxPulsing}
          onChange={handleCheckboxChange}
        >
          <Phrase id="SALES_ADDRESS.AUTHORIZED.LABEL" />
        </CheckboxStyled>
        <InfoTooltip>
          <Phrase id="SALES_ADDRESS.AUTHORIZED.TOOLTIP.HTML" />
        </InfoTooltip>
        {addressDisclaimerEnabled || theme.addressDisclaimerEnabled ? (
          <NoteContainer>
            <Phrase id="SALES_ADDRESS.DISCLAIMER" />
          </NoteContainer>
        ) : null}
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={parentCarouselItemId}
          isDisabled={!isFormValid || hasInputError}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
